import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, KanbanSquare, Plus, Users, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { CampoVerticais, CartaoVerticais, verticaisDaCriacao, type ValorVerticais } from '../../componentes/verticais';
import { Estado, Vazio } from '../../componentes/estados';
import { uuidDaUrl } from '../../componentes/filtros-lista';
import { BuscaRemota, Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dinheiro, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, useTempoRealTarefas } from './comum';
import { MarcosDoProjeto } from './Marcos';
import { VISIBILIDADES, type Projeto, type StatusProjeto } from './tipos';

const ESTADOS = ['ativos', 'arquivados', 'todos'] as const;
export const COR_STATUS: Record<StatusProjeto, CorSelo> = { planejado: 'azul', em_andamento: 'ciano', pausado: 'laranja', concluido: 'verde', cancelado: 'neutro' };

/** Projeto aberto na ficha (`?projeto=`), com o id saneado (uuid inválido é ignorado). */
function useProjetoAberto(): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const abrir = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('projeto', id);
        else n.delete('projeto');
        return n;
      },
      { replace: true },
    );
  return [uuidDaUrl(params.get('projeto')) || null, abrir];
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">
        {children}
      </TextoCortado>
    </div>
  );
}

/** Selos do projeto: só a equipe, arquivado e o status do Cadastros. Cor e texto, nunca só a cor. */
function SelosProjeto({ p }: { p: Projeto }) {
  const { t } = useTranslation('tarefas');
  return (
    <>
      {p.status && <Selo cor={COR_STATUS[p.status]}>{t(`projetos.status.${p.status}`)}</Selo>}
      {p.visibilidade === 'equipe' && (
        <Selo cor="roxo">
          <Users className="mr-1 size-3" aria-hidden />
          {t('projetos.soEquipe')}
        </Selo>
      )}
      {p.arquivado_em && <Selo>{t('projetos.arquivado')}</Selo>}
    </>
  );
}

/** Aba Projetos (Pacote 2): o quadro de cada projeto do Cadastros, com equipe, marcos, tarefas e visibilidade. */
export function Projetos() {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  useTempoRealTarefas();
  const [estado, setEstado] = useState<(typeof ESTADOS)[number]>('ativos');
  const [novo, setNovo] = useState(false);
  const [aberto, abrir] = useProjetoAberto();
  const semCadastro = <span className="text-tinta-3">{t('projetos.semAcesso')}</span>;
  const colunas: Coluna<Projeto>[] = [
    {
      chave: 'nome',
      rotulo: t('projetos.campos.projeto'),
      principal: true,
      render: (p) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{textoLivre(p.nome, p.cliente_nome)}</span>
          <SelosProjeto p={p} />
        </span>
      ),
    },
    { chave: 'cliente_nome', rotulo: t('projetos.campos.cliente'), render: (p) => (p.cadastro_visivel ? (p.cliente_nome ? nomePessoa(p.cliente_nome) : '') : semCadastro) },
    { chave: 'responsavel_nome', rotulo: t('campos.responsavel'), render: (p) => (p.cadastro_visivel ? nomePessoa(p.responsavel_nome) : semCadastro) },
    {
      chave: 'prazo',
      rotulo: t('projetos.campos.fim'),
      mono: true,
      render: (p) => (p.cadastro_visivel ? <span className={p.atrasado ? 'text-perigo' : ''}>{p.prazo ? data(p.prazo) : ''}</span> : semCadastro),
    },
    {
      chave: 'tarefas_abertas',
      rotulo: t('projetos.campos.tarefas'),
      alinhar: 'direita',
      mono: true,
      render: (p) => (
        <span className="flex flex-wrap items-center justify-end gap-2">
          {t('projetos.abertas', { count: p.tarefas_abertas, n: numero(p.tarefas_abertas) })}
          {p.tarefas_atrasadas > 0 && <Selo cor="perigo">{t('projetos.atrasadas', { count: p.tarefas_atrasadas, n: numero(p.tarefas_atrasadas) })}</Selo>}
        </span>
      ),
    },
    {
      chave: 'proximo_marco',
      rotulo: t('projetos.campos.proximoMarco'),
      render: (p) =>
        p.proximo_marco ? (
          <span className={p.proximo_marco.atrasado ? 'text-perigo' : ''}>{`${textoLivre(p.proximo_marco.nome)} · ${data(p.proximo_marco.data)}`}</span>
        ) : (
          ''
        ),
    },
    { chave: 'orcamento_centavos', rotulo: t('projetos.campos.orcamento'), oculta: true, alinhar: 'direita', mono: true, render: (p) => (p.cadastro_visivel ? dinheiro(p.orcamento_centavos, p.moeda ?? undefined) : semCadastro) },
  ];
  return (
    <>
      {/* Val5 (29/10): projetos são configuração (`tarefas.configurar`). */}
      {s.pode('tarefas.configurar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('projetos.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Projeto>
        tela="tarefas.projetos"
        caminho="/tarefas/projetos"
        chaveConsulta={[...CHAVE, 'projetos', estado]}
        params={{ estado }}
        filtros={{ estado }}
        aoAplicarFiltros={(f) => setEstado(ESTADOS.find((x) => x === f.estado) ?? 'ativos')}
        colunas={colunas}
        aoClicar={(p) => abrir(p.id)}
        buscaNaUrl
        vazio={<Vazio titulo={t('projetos.vazioTitulo')} texto={t('projetos.vazioTexto')} />}
        filtrosExtra={
          <div className="w-full sm:w-44">
            <Selecao aria-label={t('campos.situacao')} opcoes={ESTADOS.map((x) => ({ valor: x, rotulo: t(`projetos.estados.${x}`) }))} value={estado} onChange={(e) => setEstado(e.target.value as (typeof ESTADOS)[number])} />
          </div>
        }
      />
      {novo && (
        <NovoProjeto
          aoFechar={() => setNovo(false)}
          aoCriar={(id) => {
            setNovo(false);
            abrir(id);
          }}
        />
      )}
      {aberto && <DrawerProjeto key={aberto} id={aberto} aoFechar={() => abrir(null)} />}
    </>
  );
}

/** Novo projeto: nasce no Cadastros (cliente, responsável, datas e orçamento) ou liga um projeto que já existe lá. */
function NovoProjeto({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: (id: string) => void }) {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [id] = useState(() => crypto.randomUUID());
  // Ficha 2026-10-22: as verticais do quadro já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useState<ValorVerticais>(null);
  const leCadastros = s.pode('cadastros.ver');
  const ehNovo = (v: Record<string, unknown>) => v.origem !== 'existente';
  const campos: DefCampo[] = [
    ...(leCadastros
      ? [
          {
            nome: 'origem',
            rotulo: t('projetos.campos.origem'),
            tipo: 'selecao',
            obrigatorio: true,
            opcoes: [
              { valor: 'novo', rotulo: t('projetos.origens.novo') },
              { valor: 'existente', rotulo: t('projetos.origens.existente') },
            ],
          } as DefCampo,
          { nome: 'projeto_id', rotulo: t('projetos.campos.projetoCadastros'), tipo: 'busca', obrigatorio: true, visivel: (v) => !ehNovo(v), busca: { caminho: '/cadastros/projetos', rotulo: (i) => String(i.nome ?? '') } } as DefCampo,
        ]
      : []),
    { nome: 'nome', rotulo: t('projetos.campos.nomeQuadro'), tipo: 'texto', obrigatorio: true },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
    { nome: 'visibilidade', rotulo: t('projetos.campos.visibilidade'), tipo: 'selecao', obrigatorio: true, ajuda: t('projetos.visibilidadeAjuda'), opcoes: VISIBILIDADES.map((x) => ({ valor: x, rotulo: t(`projetos.visibilidades.${x}`) })) },
    { nome: 'cliente_id', rotulo: t('projetos.campos.cliente'), tipo: 'busca', largura: 'meia', visivel: ehNovo, busca: { caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i) => String(i.nome ?? '') } },
    { nome: 'responsavel_id', rotulo: t('campos.responsavel'), tipo: 'busca', largura: 'meia', visivel: ehNovo, busca: { caminho: '/membros', rotulo: (i) => String(i.nome ?? ''), pessoa: true } },
    { nome: 'inicio', rotulo: t('projetos.campos.inicio'), tipo: 'data', largura: 'terco', visivel: ehNovo },
    { nome: 'prazo', rotulo: t('projetos.campos.fim'), tipo: 'data', largura: 'terco', visivel: ehNovo },
    { nome: 'orcamento_centavos', rotulo: t('projetos.campos.orcamento'), tipo: 'dinheiro', largura: 'terco', visivel: ehNovo },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('projetos.novo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-projeto-tarefas">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{t('projetos.novoTexto')}</p>
      <Formulario
        id="form-projeto-tarefas"
        campos={campos}
        inicial={{ origem: 'novo', visibilidade: 'todos' }}
        aoEnviar={async (v) => {
          const existente = v.origem === 'existente';
          await post('/tarefas/projetos', {
            id,
            nome: v.nome,
            descricao: typeof v.descricao === 'string' && v.descricao.trim() ? v.descricao : null,
            visibilidade: v.visibilidade,
            ...(existente
              ? { projeto_id: v.projeto_id }
              : { cliente_id: v.cliente_id || null, responsavel_id: v.responsavel_id || null, inicio: v.inicio || null, prazo: v.prazo || null, orcamento_centavos: v.orcamento_centavos ?? null }),
            ...verticaisDaCriacao(verticais),
          });
          await qc.invalidateQueries({ queryKey: CHAVE });
          toast.sucesso(t('projetos.criado'));
          aoCriar(id);
        }}
      >
        <CampoVerticais valor={verticais} aoMudar={setVerticais} />
      </Formulario>
    </Drawer>
  );
}

/** Equipe do projeto: quem vê o quadro quando a visibilidade é "só a equipe". Cada mudança salva na hora. */
function Equipe({ p, podeEditar, aoMudar }: { p: Projeto; podeEditar: boolean; aoMudar: () => Promise<void> }) {
  const { t } = useTranslation('tarefas');
  const toast = useToast();
  const [adicionando, setAdicionando] = useState<string | null>(null);
  const salvar = useMutation({
    mutationFn: (membros: string[]) => api('PUT', `/tarefas/projetos/${p.id}/membros`, { membros }),
    onSuccess: async () => {
      setAdicionando(null);
      await aoMudar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const ids = p.membros.map((m) => m.id);
  return (
    <section aria-labelledby={`equipe-${p.id}`} className="flex flex-col gap-2">
      <h3 id={`equipe-${p.id}`} className="text-sm font-semibold">
        {t('projetos.equipe')}
      </h3>
      <p className="text-xs text-tinta-3">{p.visibilidade === 'equipe' ? t('projetos.equipeRestrita') : t('projetos.equipeAberta')}</p>
      {p.membros.length === 0 && <p className="text-sm text-tinta-3">{t('projetos.semMembros')}</p>}
      <ul className="flex flex-col divide-y divide-borda">
        {p.membros.map((m) => (
          <li key={m.id} className="flex min-w-0 items-center justify-between gap-2 py-1.5 text-sm">
            <TextoCortado className="min-w-0">{nomePessoa(m.nome)}</TextoCortado>
            {podeEditar && (
              <Botao
                tamanho="sm"
                variante="fantasma"
                aria-label={t('projetos.removerMembro', { nome: nomePessoa(m.nome) })}
                carregando={salvar.isPending}
                onClick={() => salvar.mutate(ids.filter((x) => x !== m.id))}
                icone={<X className="size-4" />}
              />
            )}
          </li>
        ))}
      </ul>
      {podeEditar && (
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (adicionando && !ids.includes(adicionando)) salvar.mutate([...ids, adicionando]);
          }}
        >
          <div className="min-w-0 flex-1">
            <BuscaRemota
              id={`membro-${p.id}`}
              aria-describedby={`equipe-${p.id}`}
              def={{ caminho: '/membros', rotulo: (i) => String(i.nome ?? ''), pessoa: true }}
              valor={adicionando}
              aoMudar={(v) => setAdicionando(v)}
              placeholder={t('projetos.buscarMembro')}
            />
          </div>
          <Botao type="submit" icone={<Plus className="size-4" />} carregando={salvar.isPending}>
            {t('projetos.adicionarMembro')}
          </Botao>
        </form>
      )}
    </section>
  );
}

/** Ficha do projeto: dados do Cadastros, edição, equipe, marcos, atalho para o kanban e o chatter do quadro. */
function DrawerProjeto({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [arquivando, setArquivando] = useState(false);
  const q = useQuery({ queryKey: [...CHAVE, 'projeto', id], queryFn: () => get<Projeto>(`/tarefas/projetos/${id}`) });
  const podeEditar = s.pode('tarefas.configurar');
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: CHAVE });
    await qc.invalidateQueries({ queryKey: ['chatter', 'tarefas.quadro', id] });
  };
  const estado = useMutation({
    mutationFn: (acao: 'arquivar' | 'reabrir') => post(`/tarefas/projetos/${id}/${acao}`),
    onSuccess: async (_r, acao) => {
      setArquivando(false);
      toast.sucesso(t(acao === 'arquivar' ? 'projetos.arquivadoAviso' : 'projetos.reabertoAviso'));
      await recarregar();
    },
    onError: (e) => {
      setArquivando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const p = q.data;
  const editaCadastro = !!p?.cadastro_visivel && s.pode('cadastros.editar');
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('projetos.campos.nomeQuadro'), tipo: 'texto', obrigatorio: true },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
    { nome: 'visibilidade', rotulo: t('projetos.campos.visibilidade'), tipo: 'selecao', obrigatorio: true, ajuda: t('projetos.visibilidadeAjuda'), opcoes: VISIBILIDADES.map((x) => ({ valor: x, rotulo: t(`projetos.visibilidades.${x}`) })) },
    ...(editaCadastro
      ? ([
          { nome: 'cliente_id', rotulo: t('projetos.campos.cliente'), tipo: 'busca', largura: 'meia', busca: { caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i: Record<string, unknown>) => String(i.nome ?? '') } },
          { nome: 'responsavel_id', rotulo: t('campos.responsavel'), tipo: 'busca', largura: 'meia', busca: { caminho: '/membros', rotulo: (i: Record<string, unknown>) => String(i.nome ?? ''), pessoa: true } },
          { nome: 'inicio', rotulo: t('projetos.campos.inicio'), tipo: 'data', largura: 'terco' },
          { nome: 'prazo', rotulo: t('projetos.campos.fim'), tipo: 'data', largura: 'terco' },
          { nome: 'orcamento_centavos', rotulo: t('projetos.campos.orcamento'), tipo: 'dinheiro', largura: 'terco' },
        ] as DefCampo[])
      : []),
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={p ? textoLivre(p.nome, p.cliente_nome) : t('projetos.carregando')}
      rodape={
        p && podeEditar ? (
          <>
            {p.arquivado_em ? (
              <Botao className="mr-auto" icone={<ArchiveRestore className="size-4" />} carregando={estado.isPending} onClick={() => estado.mutate('reabrir')}>
                {t('projetos.reabrir')}
              </Botao>
            ) : (
              <Botao variante="perigo" className="mr-auto" icone={<Archive className="size-4" />} onClick={() => setArquivando(true)}>
                {t('projetos.arquivar')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-editar-projeto">
              {tn('comum.salvar')}
            </Botao>
          </>
        ) : undefined
      }
    >
      <Estado consulta={q}>
        {(p) => (
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <SelosProjeto p={p} />
              <Link to={`/operacao/tarefas/kanban?quadro=${p.id}`} className="inline-flex items-center gap-1 text-sm text-acento-forte hover:underline">
                <KanbanSquare className="size-4" aria-hidden />
                {t('projetos.abrirKanban')}
              </Link>
            </div>
            {p.arquivado_em && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('projetos.arquivadoTexto')}</p>}
            {p.descricao && <p className="text-sm break-words text-tinta-2">{textoLivre(p.descricao, p.cliente_nome)}</p>}

            <dl className="divide-y divide-borda">
              {p.cadastro_visivel ? (
                <>
                  <Linha rotulo={t('projetos.campos.cliente')}>{p.cliente_nome ? nomePessoa(p.cliente_nome) : '-'}</Linha>
                  <Linha rotulo={t('campos.responsavel')}>{p.responsavel_nome ? nomePessoa(p.responsavel_nome) : '-'}</Linha>
                  <Linha rotulo={t('projetos.campos.inicio')}>{p.inicio ? data(p.inicio) : '-'}</Linha>
                  <Linha rotulo={t('projetos.campos.fim')}>{p.prazo ? `${data(p.prazo)}${p.atrasado ? ` (${t('kanban.atrasado')})` : ''}` : '-'}</Linha>
                  <Linha rotulo={t('projetos.campos.orcamento')}>{p.orcamento_centavos === null ? '-' : dinheiro(p.orcamento_centavos, p.moeda ?? undefined)}</Linha>
                </>
              ) : (
                <Linha rotulo={t('projetos.campos.cadastro')}>{t('projetos.semAcesso')}</Linha>
              )}
              <Linha rotulo={t('projetos.campos.tarefas')}>
                {`${t('projetos.abertas', { count: p.tarefas_abertas, n: numero(p.tarefas_abertas) })} · ${t('projetos.concluidas', { count: p.tarefas_concluidas, n: numero(p.tarefas_concluidas) })}`}
              </Linha>
              <Linha rotulo={t('projetos.campos.marcos')}>{t('projetos.marcosFeitos', { feitos: numero(p.marcos_concluidos), total: numero(p.marcos_total) })}</Linha>
            </dl>

            {podeEditar && (
              <Formulario
                id="form-editar-projeto"
                campos={campos}
                inicial={{ nome: p.nome, descricao: p.descricao ?? '', visibilidade: p.visibilidade, cliente_id: p.cliente_id ?? '', responsavel_id: p.responsavel_id ?? '', inicio: p.inicio ?? '', prazo: p.prazo ?? '', orcamento_centavos: p.orcamento_centavos }}
                rotulosAtuais={{ cliente_id: p.cliente_nome ?? '', responsavel_id: p.responsavel_nome ?? '' }}
                aoEnviar={async (v) => {
                  await patch(`/tarefas/projetos/${p.id}`, {
                    nome: v.nome,
                    descricao: typeof v.descricao === 'string' && v.descricao.trim() ? v.descricao : null,
                    visibilidade: v.visibilidade,
                    ...(editaCadastro
                      ? { cliente_id: v.cliente_id || null, responsavel_id: v.responsavel_id || null, inicio: v.inicio || null, prazo: v.prazo || null, orcamento_centavos: v.orcamento_centavos ?? null }
                      : {}),
                  });
                  toast.sucesso(tn('comum.salvo'));
                  await recarregar();
                }}
              />
            )}

            <Equipe p={p} podeEditar={podeEditar} aoMudar={recarregar} />
            <MarcosDoProjeto projeto={p} />
            <CartaoVerticais entidade="tarefas.quadro" registroId={p.id} podeEditar={podeEditar} />

            <div className="border-t border-borda pt-4">
              <Chatter entidade="tarefas.quadro" registroId={p.id} nomes={[p.cliente_nome]} titulo={p.nome} />
            </div>
          </div>
        )}
      </Estado>
      <Confirmar
        aberto={arquivando}
        aoFechar={() => setArquivando(false)}
        aoConfirmar={() => estado.mutate('arquivar')}
        titulo={t('projetos.arquivarTitulo')}
        texto={t('projetos.arquivarTexto')}
        perigoso
        carregando={estado.isPending}
      />
    </Drawer>
  );
}
